import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { useCart } from '../../../contexts/CartContext';

const providerConfig = {
  paypal: {
    name: 'PayPal Sandbox',
    logo: 'https://www.paypalobjects.com/webstatic/icon/pp258.png',
    accent: '#003087'
  },
  stripe: {
    name: 'Stripe Test Checkout',
    logo: 'https://brandslogos.com/wp-content/uploads/images/large/stripe-logo.png',
    accent: '#635BFF'
  },
  razorpay: {
    name: 'Razorpay Sandbox',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9a/Razorpay_logo.png',
    accent: '#0F5EF7'
  }
};

const PaymentProcessPage = () => {
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);

  const paymentId = searchParams.get('paymentId');
  const providerKey = searchParams.get('provider');

  const provider = useMemo(() => providerConfig[providerKey] || null, [providerKey]);

  if (!paymentId || !provider) {
    navigate('/user/cart', { replace: true });
    return null;
  }

  const handleCompletion = async (status) => {
    setIsProcessing(true);
    try {
      const transactionId = `${providerKey.toUpperCase()}-SANDBOX-${Date.now()}`;
      await api.payments.complete({
        paymentId,
        status,
        transactionId,
        details: {
          provider: providerKey,
          reference: transactionId
        }
      });

      await refreshCart();

      if (status === 'success') {
        navigate(`/user/payment-success?paymentId=${paymentId}`, { replace: true });
      } else {
        navigate(`/user/payment-failure?paymentId=${paymentId}`, { replace: true });
      }
    } catch (error) {
      console.error('Payment completion error', error);
      const errorMessage = error.response?.data?.message || 'Something went wrong while finalising the payment.';
      toast.error(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <div className="container-fluid px-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-dark mb-1">Sandbox Payment Checkout</h4>
          <p className="text-muted mb-0">Simulate the final step with {provider.name}. This environment is for testing purposes only.</p>
        </div>
        <Button variant="link" className="text-decoration-none" onClick={() => navigate('/user/cart')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Cart
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4 d-flex flex-column flex-md-row align-items-center">
          <div className="me-md-4 mb-3 mb-md-0">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '96px',
                height: '96px',
                backgroundColor: '#f5f7fb'
              }}
            >
              <img
                src={provider.logo}
                alt={`${provider.name} logo`}
                style={{ maxWidth: '72px', maxHeight: '72px', objectFit: 'contain' }}
              />
            </div>
          </div>
          <div className="flex-grow-1">
            <h5 className="fw-semibold text-dark mb-3">{provider.name}</h5>
            <p className="text-muted mb-4">
              This sandbox simulates the payment gateway experience. Use the controls below to mark the payment as successful or failed. No real transaction will occur.
            </p>
            <div className="d-flex flex-column flex-md-row gap-3">
              <Button
                variant="success"
                className="fw-semibold"
                disabled={isProcessing}
                onClick={() => handleCompletion('success')}
              >
                {isProcessing ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle me-2"></i>
                    Simulate Success
                  </>
                )}
              </Button>
              <Button
                variant="outline-danger"
                className="fw-semibold"
                disabled={isProcessing}
                onClick={() => handleCompletion('failed')}
              >
                <i className="fas fa-times-circle me-2"></i>
                Simulate Failure
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaymentProcessPage;

